import type {
  BrowseSubscriptionPlanResponse,
  Plan,
  PlanCardConfig,
  ProductCards,
} from '@aibees/shared/definition';
import { buildFeatureGroups } from './build-feature-groups';
import { isFreePlanSet, planFamilyKey, productFamilyKey } from './utils';

const EMPTY_OPTIONS: BrowseSubscriptionPlanResponse = [];

// Human-readable tagline badges keyed by the raw catalog `metadata.label`.
// Control flags like "MOST_POPULAR" (rendered by the Most Popular tag)
// intentionally have no entry, so no raw label leaks into the UI.
const TAGLINE_BY_LABEL: Record<string, string> = {
  AGENCIES_GROWTH_TEAMS: 'Agencies & Growth Teams',
};

export function buildPlanCardConfig(
  productId: string,
  cards: ProductCards,
  plans: Plan[]
): PlanCardConfig | null {
  const product = cards.products.find((p) => p.id === productId);
  if (!product) return null;

  const familyKey = productFamilyKey(product);
  const productPlans = plans
    .filter((p) => {
      const planKey = planFamilyKey(p);
      return planKey && planKey === familyKey;
    })
    .sort((a, b) => (a.amount ?? 0) - (b.amount ?? 0));

  const cheapest = productPlans[0];
  const isFree = isFreePlanSet(productPlans);
  // `request` is backend-emitted free text — non-numeric or missing would make
  // Number() NaN and render raw on the card.
  const defaultCredit = cheapest ? Number(cheapest.metadata.request) || 0 : 0;
  const monthlyCreditRate =
    cheapest && defaultCredit > 0
      ? (cheapest.amount ?? 0) / (defaultCredit / 1000)
      : 0;

  return {
    title: `${product.name} Plan`,
    subtitle: product.description ?? '',
    monthlyCreditRate,
    yearlyCreditRate: 0,
    tagline: product.metadata.label
      ? TAGLINE_BY_LABEL[product.metadata.label]
      : undefined,
    priceDescription: isFree ? 'Always free' : 'Per Month',
    featureGroups: buildFeatureGroups(productId, cards),
    defaultCredit,
    options: EMPTY_OPTIONS,
    buttonText: isFree ? 'Get Started' : `Switch to ${product.name}`,
    disabled: false,
  };
}
